import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from 'reactstrap'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { fetchSettings, saveSettings, previewTemplate, fetchTenants } from '../../actions/cspRenewalsActions'

// Tight toolbar — admins are editing renewal email copy, not building a CMS.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
}

const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link']

const TOKENS = [
  { token: '{{ContactName}}', desc: "Recipient contact's first name (or full name if first is empty)" },
  { token: '{{CompanyName}}', desc: 'CW company name (cached on the DD tenant row)' },
  { token: '{{RenewalMonth}}', desc: 'Renewal month, formatted e.g. "May 2026"' },
  { token: '{{SubscriptionList}}', desc: 'Service-rendered HTML table of subscriptions in this batch' },
]

const TokenReference = () => (
  <div className="border rounded bg-light p-3 small">
    <div className="text-uppercase text-muted font-weight-bold mb-2" style={{ fontSize: '0.75rem', letterSpacing: 0.5 }}>
      Available tokens
    </div>
    {TOKENS.map((t) => (
      <div key={t.token} className="mb-1">
        <code>{t.token}</code> <span className="text-muted">— {t.desc}</span>
      </div>
    ))}
    <div className="text-muted mt-2 small">
      Tokens are substituted at send time. <code>{'{{SubscriptionList}}'}</code> is the only loop —
      everything else is a single-value swap.
    </div>
  </div>
)

const formatDaysList = (arr) => (arr || []).join(', ')
const parseDaysList = (text) =>
  (text || '')
    .split(/[, ]+/)
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n) && n > 0)

const TemplateEditor = ({ label, value, onChange, onPreview, previewBusy, tenantOptions, tenantId, onTenantChange }) => (
  <>
    <Label className="font-weight-bold mt-2">{label} heading</Label>
    <Input
      type="text"
      value={value?.heading ?? ''}
      onChange={(e) => onChange({ ...value, heading: e.target.value })}
      bsSize="sm"
      placeholder="e.g. Microsoft 365 Renewals"
    />
    <small className="text-muted">
      Shown as the H1 in the email's blue header, beside the OSIT logo.
    </small>
    <Label className="font-weight-bold mt-3">{label} subject</Label>
    <Input
      type="text"
      value={value?.subject ?? ''}
      onChange={(e) => onChange({ ...value, subject: e.target.value })}
      bsSize="sm"
    />
    <small className="text-muted">
      Email subject line (what shows in the inbox subject column).
    </small>
    <Label className="font-weight-bold mt-3">{label} body</Label>
    <ReactQuill
      theme="snow"
      modules={QUILL_MODULES}
      formats={QUILL_FORMATS}
      value={value?.body ?? ''}
      onChange={(html) => onChange({ ...value, body: html })}
    />
    <small className="text-muted">
      Sits inside the chrome's body cell. Use <code>{'{{ContactName}}'}</code>,{' '}
      <code>{'{{CompanyName}}'}</code>, <code>{'{{RenewalMonth}}'}</code>,{' '}
      <code>{'{{SubscriptionList}}'}</code> tokens.
    </small>

    <div className="d-flex align-items-center mt-3 gap-2">
      <Label className="font-weight-bold mb-0 mr-2">Preview as:</Label>
      <Input
        type="select"
        value={tenantId || ''}
        onChange={(e) => onTenantChange(e.target.value || null)}
        bsSize="sm"
        style={{ flex: 1, maxWidth: 360 }}
      >
        <option value="">Auto (first mapped tenant)</option>
        {(tenantOptions || []).map((t) => (
          <option key={t.id} value={t.id}>
            {t.companyName || t.tenantName || t.tenantId}
          </option>
        ))}
      </Input>
      <Button
        size="sm"
        color="outline-primary"
        className="ml-2"
        disabled={previewBusy}
        onClick={onPreview}
      >
        {previewBusy ? 'Rendering…' : 'Preview'}
      </Button>
    </div>
  </>
)

const PreviewPane = ({ preview }) => {
  if (!preview) return null
  return (
    <div className="mt-3 border rounded">
      <div className="bg-light px-3 py-2 border-bottom">
        <div className="text-uppercase text-muted small font-weight-bold" style={{ fontSize: '0.7rem', letterSpacing: 0.5 }}>
          Subject
        </div>
        <div className="font-weight-bold">{preview.subject}</div>
      </div>
      {(preview.notes || []).length > 0 && (
        <div className="bg-warning-subtle border-bottom px-3 py-2 small">
          {(preview.notes || []).map((n, i) => (
            <div key={i}>⚠ {n}</div>
          ))}
        </div>
      )}
      {/* Quill output already sanitises to a constrained tag set; this is admin-only
          and the same HTML the dispatcher will render in real emails. */}
      <div
        className="px-3 py-3"
        style={{ background: '#fff' }}
        dangerouslySetInnerHTML={{ __html: preview.body }}
      />
    </div>
  )
}

const SettingsModal = ({ isOpen, toggle, isAdmin }) => {
  const dispatch = useDispatch()
  const settings = useSelector((s) => s.cspRenewals.settings)
  const settingsLoading = useSelector((s) => s.cspRenewals.settingsLoading)
  const settingsSaving = useSelector((s) => s.cspRenewals.settingsSaving)

  const tenants = useSelector((s) => s.cspRenewals.tenants) || []
  const mappedTenants = tenants.filter((t) => !!t.companyId)

  const [tab, setTab] = useState('general')
  const [draft, setDraft] = useState(null)
  const [previewTenantId, setPreviewTenantId] = useState(null)
  const [previewBusy, setPreviewBusy] = useState(false)
  const [previewData, setPreviewData] = useState(null)

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchSettings())
      // Settings modal is sometimes opened without first visiting the
      // tenants page, so the tenant list might not be loaded yet.
      if (tenants.length === 0) dispatch(fetchTenants())
    }
  }, [isOpen, dispatch])

  // Seed the draft from the loaded server state once it arrives, then leave
  // it alone so admin edits aren't blown away by stray re-renders.
  useEffect(() => {
    if (isOpen && settings && !draft) setDraft({ ...settings })
  }, [isOpen, settings, draft])

  // Reset draft + preview state on close so the next open starts fresh.
  useEffect(() => {
    if (!isOpen) {
      setDraft(null)
      setPreviewData(null)
    }
  }, [isOpen])

  // Drop the preview when admin switches tabs - it was rendered for the
  // tab you just left and shouldn't carry over.
  useEffect(() => {
    setPreviewData(null)
  }, [tab])

  const handlePreview = async (track) => {
    if (!draft) return
    const template = track === 'yearly' ? draft.yearlyTemplate : draft.monthlyTemplate
    setPreviewBusy(true)
    setPreviewData(null)
    const result = await dispatch(previewTemplate({ track, tenantId: previewTenantId, template }))
    setPreviewBusy(false)
    if (result) setPreviewData(result)
  }

  if (!draft) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>CSP Renewal Settings</ModalHeader>
        <ModalBody>
          {settingsLoading
            ? <div className="text-muted">Loading…</div>
            : <div className="text-muted">No settings loaded.</div>}
        </ModalBody>
      </Modal>
    )
  }

  const handleSave = () => {
    dispatch(saveSettings(draft))
  }

  const setDraftField = (patch) => setDraft((d) => ({ ...d, ...patch }))
  const setTemplateField = (track, value) =>
    setDraft((d) => ({ ...d, [track]: value }))

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings)

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        CSP Renewal Settings
        {dirty && <Badge color="warning" className="ml-2">Unsaved</Badge>}
      </ModalHeader>
      <ModalBody>
        {(draft.testMode || draft.isTestModeForced) && (
          <div className="alert alert-warning py-2 mb-3 small">
            Renewal emails will be sent only to the BCC mailbox ({draft.bccMailbox || '—'});
          </div>
        )}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              active={tab === 'general'}
              onClick={() => setTab('general')}
              style={{ cursor: 'pointer' }}
            >
              General
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              active={tab === 'yearly'}
              onClick={() => setTab('yearly')}
              style={{ cursor: 'pointer' }}
            >
              Yearly template
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              active={tab === 'monthly'}
              onClick={() => setTab('monthly')}
              style={{ cursor: 'pointer' }}
            >
              Monthly template
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={tab}>
          <TabPane tabId="general">
            <Row>
              <Col md={6}>
                <Label className="font-weight-bold">Sender email</Label>
                <Input
                  type="email"
                  value={draft.senderEmail ?? ''}
                  onChange={(e) => setDraftField({ senderEmail: e.target.value })}
                  bsSize="sm"
                />
                <small className="text-muted">From-address on every renewal email.</small>
              </Col>
              <Col md={6}>
                <Label className="font-weight-bold">BCC mailbox</Label>
                <Input
                  type="email"
                  value={draft.bccMailbox ?? ''}
                  onChange={(e) => setDraftField({ bccMailbox: e.target.value })}
                  bsSize="sm"
                />
                <small className="text-muted">Shared mailbox copied on every send.</small>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Label className="font-weight-bold">Yearly notification days</Label>
                <Input
                  type="text"
                  value={formatDaysList(draft.yearlyNotificationDays)}
                  onChange={(e) =>
                    setDraftField({ yearlyNotificationDays: parseDaysList(e.target.value) })
                  }
                  bsSize="sm"
                />
                <small className="text-muted">
                  Days before renewal-month start to fire yearly notices. e.g. <code>60, 30, 7</code>.
                </small>
              </Col>
              <Col md={6}>
                <Label className="font-weight-bold">Monthly notification days</Label>
                <Input
                  type="text"
                  value={formatDaysList(draft.monthlyNotificationDays)}
                  onChange={(e) =>
                    setDraftField({ monthlyNotificationDays: parseDaysList(e.target.value) })
                  }
                  bsSize="sm"
                />
                <small className="text-muted">e.g. <code>7, 3</code>.</small>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col>
                <Label className="font-weight-bold mb-1">Test mode</Label>
                {draft.isTestModeForced && (
                  <div className="alert alert-warning py-2 mb-2 small">
                    <strong>Test mode is enforced via deployment configuration.</strong> No real
                    emails will be sent regardless of the toggle below; the next deploy without{' '}
                    <code>CSPRenewal:ForceTestMode = true</code> will activate the saved value.
                  </div>
                )}
                <div className="d-flex align-items-center">
                  <Input
                    type="checkbox"
                    className="m-0 mr-2"
                    style={{ position: 'relative', marginTop: 0, transform: 'scale(1.2)' }}
                    checked={!!draft.testMode}
                    onChange={(e) => setDraftField({ testMode: e.target.checked })}
                  />
                  <span className="small">
                    Send all renewal emails only to the BCC mailbox; suppress real recipients.
                  </span>
                </div>
              </Col>
            </Row>
          </TabPane>

          <TabPane tabId="yearly">
            <TemplateEditor
              label="Yearly"
              value={draft.yearlyTemplate}
              onChange={(v) => setTemplateField('yearlyTemplate', v)}
              onPreview={() => handlePreview('yearly')}
              previewBusy={previewBusy}
              tenantOptions={mappedTenants}
              tenantId={previewTenantId}
              onTenantChange={setPreviewTenantId}
            />
            <PreviewPane preview={previewData} />
            <div className="mt-3">
              <TokenReference />
            </div>
          </TabPane>

          <TabPane tabId="monthly">
            <TemplateEditor
              label="Monthly"
              value={draft.monthlyTemplate}
              onChange={(v) => setTemplateField('monthlyTemplate', v)}
              onPreview={() => handlePreview('monthly')}
              previewBusy={previewBusy}
              tenantOptions={mappedTenants}
              tenantId={previewTenantId}
              onTenantChange={setPreviewTenantId}
            />
            <PreviewPane preview={previewData} />
            <div className="mt-3">
              <TokenReference />
            </div>
          </TabPane>
        </TabContent>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={settingsSaving}>
          Close
        </Button>
        {isAdmin && (
          <Button color="primary" onClick={handleSave} disabled={!dirty || settingsSaving}>
            {settingsSaving ? 'Saving…' : 'Save'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  )
}

export default SettingsModal
